import { Button, Card, Col, Form, Input, Row, Typography, Select, DatePicker, Switch, Tabs, Table, Checkbox, Modal } from "antd";
import { Fragment, useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { addDiscountMaster } from "../../redux/actions/discountMaster/addDiscountMasterAction";
import { updateDiscountMaster } from "../../redux/actions/discountMaster/updateDiscountMasterAction";
import { loadSingleDiscountMaster } from "../../redux/actions/discountMaster/detailDiscountMasterAction";
import { getDiscountByPublisherAndParty } from "../../redux/actions/discountMaster/getDiscountByPublisherPartyAction";
import { loadAllBookPublisher } from "../../redux/actions/bookPublisher/getBookPublisherAction";
import { loadAllCustomer } from "../../redux/actions/customer/getCustomerAction";
import { loadSuppliers } from "../../redux/actions/supplier/getSuppliersAction";
import AddBookPublisher from "../bookPublisher/addBookPublisher";
import moment from "moment";

const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

const AddDiscountMaster = ({ onSuccess = null, inModal = false, onCancel = null }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const editMode = !inModal && Boolean(id);

  const { Title } = Typography;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [discountType, setDiscountType] = useState("");
  const [activeTab, setActiveTab] = useState(inModal ? "bulk" : "single");
  const [isAddPublisherModalOpen, setIsAddPublisherModalOpen] = useState(false);
  const [prefillPublisherText, setPrefillPublisherText] = useState("");

  // Bulk mode states
  const [selectedPublishers, setSelectedPublishers] = useState([]);
  const [publisherDiscounts, setPublisherDiscounts] = useState({});
  const [applyFlatDiscount, setApplyFlatDiscount] = useState(false);
  const [flatDiscountValue, setFlatDiscountValue] = useState("");
  const [isQuotationModalOpen, setIsQuotationModalOpen] = useState(false);

  const discount = useSelector((state) => state.discountMasters?.discount);
  const publishers = useSelector((state) => state.bookPublishers?.list);
  const customers = useSelector((state) => state.customers?.list);
  const suppliers = useSelector((state) => state.suppliers?.list);

  useEffect(() => {
    dispatch(loadAllBookPublisher({ page: 1, limit: 1000 }));
    dispatch(loadAllCustomer({ page: 1, limit: 1000, status: true }));
    dispatch(loadSuppliers({ page: 1, limit: 1000, status: true }));

    if (editMode && id) {
      dispatch(loadSingleDiscountMaster(id));
    }
  }, [dispatch, editMode, id]);

  useEffect(() => {
    if (editMode && discount) {
      setDiscountType(discount.discount_type);
      form.setFieldsValue({
        discount_type: discount.discount_type,
        publisher_id: discount.publisher_id,
        customer_id: discount.customer_id,
        supplier_id: discount.supplier_id,
        discount_value: discount.discount_value,
        discount_unit: discount.discount_unit,
        status: discount.status,
        effective_from: discount.effective_from ? moment(discount.effective_from) : null,
        effective_to: discount.effective_to ? moment(discount.effective_to) : null,
        description: discount.description,
      });
    }
  }, [editMode, discount, form]);

  // Prefill discount in Single Add when publisher and customer/supplier are selected
  const prefillingSingle = useRef(false);
  const prefillSingleDiscount = (publisherId, customerId, supplierId) => {
    if (editMode || activeTab !== "single" || prefillingSingle.current) return;
    const type = form.getFieldValue("discount_type");
    const pubId = publisherId ?? form.getFieldValue("publisher_id");
    const custId = customerId ?? form.getFieldValue("customer_id");
    const supId = supplierId ?? form.getFieldValue("supplier_id");
    const hasParty = type === "sale" ? custId : type === "purchase" ? supId : false;
    if (!type || !pubId || !hasParty) return;
    prefillingSingle.current = true;
    dispatch(
      getDiscountByPublisherAndParty({
        discount_type: type,
        publisher_id: pubId,
        customer_id: type === "sale" ? custId : undefined,
        supplier_id: type === "purchase" ? supId : undefined,
      })
    ).then((data) => {
      prefillingSingle.current = false;
      if (data && (data.discount_value != null || data.discount_unit || data.effective_from || data.effective_to || data.description)) {
        form.setFieldsValue({
          discount_value: data.discount_value,
          discount_unit: data.discount_unit || form.getFieldValue("discount_unit"),
          effective_from: data.effective_from ? moment(data.effective_from) : form.getFieldValue("effective_from"),
          effective_to: data.effective_to ? moment(data.effective_to) : form.getFieldValue("effective_to"),
          description: data.description ?? form.getFieldValue("description"),
        });
      }
    }).catch(() => { prefillingSingle.current = false; });
  };

  // Prefill discounts in Bulk Add for selected publishers + customer/supplier
  const prefillingBulk = useRef(false);
  const prefillBulkDiscounts = (publisherIds) => {
    const ids = publisherIds ?? selectedPublishers;
    if (activeTab !== "bulk" || ids.length === 0 || prefillingBulk.current) return;
    const type = form.getFieldValue("discount_type");
    const customerId = form.getFieldValue("customer_id");
    const supplierId = form.getFieldValue("supplier_id");
    const hasParty = type === "sale" ? customerId : type === "purchase" ? supplierId : false;
    if (!type || !hasParty) return;
    prefillingBulk.current = true;
    const promises = ids.map((pubId) =>
      dispatch(
        getDiscountByPublisherAndParty({
          discount_type: type,
          publisher_id: pubId,
          customer_id: type === "sale" ? customerId : undefined,
          supplier_id: type === "purchase" ? supplierId : undefined,
        })
      )
    );
    Promise.all(promises).then((results) => {
      prefillingBulk.current = false;
      setPublisherDiscounts((prev) => {
        const next = { ...prev };
        results.forEach((data, i) => {
          const pubId = ids[i];
          if (data && data.discount_value != null) {
            next[pubId] = String(data.discount_value);
          }
        });
        return next;
      });
    }).catch(() => { prefillingBulk.current = false; });
  };

  const handleFlatDiscountChange = (e) => {
    const value = e.target.value;
    setFlatDiscountValue(value);
    if (applyFlatDiscount) {
      const newDiscounts = { ...publisherDiscounts };
      selectedPublishers.forEach(pubId => {
        newDiscounts[pubId] = value;
      });
      setPublisherDiscounts(newDiscounts);
    }
  };

  const handleApplyFlatDiscountToggle = (e) => {
    const checked = e.target.checked;
    setApplyFlatDiscount(checked);
    if (checked && flatDiscountValue !== "") {
      const newDiscounts = { ...publisherDiscounts };
      selectedPublishers.forEach(pubId => {
        newDiscounts[pubId] = flatDiscountValue;
      });
      setPublisherDiscounts(newDiscounts);
    }
  };

  const handlePublisherDiscountChange = (pubId, value) => {
    setPublisherDiscounts(prev => ({ ...prev, [pubId]: value }));
  };

  const pbSelection = {
    selectedRowKeys: selectedPublishers,
    onChange: (selectedRowKeys) => {
      setSelectedPublishers(selectedRowKeys);
      if (applyFlatDiscount && flatDiscountValue !== "") {
        const newDiscounts = { ...publisherDiscounts };
        selectedRowKeys.forEach(pubId => {
          if (!newDiscounts[pubId] || newDiscounts[pubId] === "") {
            newDiscounts[pubId] = flatDiscountValue;
          }
        });
        setPublisherDiscounts(newDiscounts);
      }
      if (selectedRowKeys.length > 0) {
        setTimeout(() => prefillBulkDiscounts(selectedRowKeys), 0);
      }
    },
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      if (activeTab === "bulk") {
        if (selectedPublishers.length === 0) {
          alert("Please select at least one publisher.");
          setLoading(false);
          return;
        }

        // Validate individual discounts
        let hasError = false;
        const payloadArray = selectedPublishers.map(pubId => {
          const val = publisherDiscounts[pubId];
          const unit = values.discount_unit;

          if (val === undefined || val === "") {
            hasError = true;
          } else if (unit === "percentage" && (val < 0 || val > 100)) {
            hasError = true;
          } else if (unit === "fixed" && val < 0) {
            hasError = true;
          }

          return {
            discount_type: values.discount_type,
            publisher_id: pubId,
            customer_id: values.discount_type === "sale" ? values.customer_id : null,
            supplier_id: values.discount_type === "purchase" ? values.supplier_id : null,
            discount_value: val,
            discount_unit: values.discount_unit,
            status: values.status !== undefined ? values.status : true,
            effective_from: values.effective_from ? values.effective_from.format("YYYY-MM-DD") : null,
            effective_to: values.effective_to ? values.effective_to.format("YYYY-MM-DD") : null,
            description: values.description,
          };
        });

        if (hasError) {
          alert("Please enter valid discount values for all selected publishers.");
          setLoading(false);
          return;
        }

        const resp = await dispatch(addDiscountMaster(payloadArray));
        if (resp?.message === "success") {
          setLoading(false);
          form.resetFields();
          setSelectedPublishers([]);
          setPublisherDiscounts({});
          setFlatDiscountValue("");
          setApplyFlatDiscount(false);
          if (inModal && onSuccess) onSuccess();
          else if (!inModal) navigate("/discount-master");
        } else {
          setLoading(false);
        }

      } else {
        const payload = {
          discount_type: values.discount_type,
          publisher_id: values.publisher_id,
          customer_id: values.discount_type === "sale" ? values.customer_id : null,
          supplier_id: values.discount_type === "purchase" ? values.supplier_id : null,
          discount_value: values.discount_value,
          discount_unit: values.discount_unit,
          status: values.status !== undefined ? values.status : true,
          effective_from: values.effective_from ? values.effective_from.format("YYYY-MM-DD") : null,
          effective_to: values.effective_to ? values.effective_to.format("YYYY-MM-DD") : null,
          description: values.description,
        };

        let resp;
        if (editMode) {
          resp = await dispatch(updateDiscountMaster(id, payload));
        } else {
          resp = await dispatch(addDiscountMaster(payload));
        }

        if (resp?.message === "success") {
          setLoading(false);
          form.resetFields();
          if (inModal && onSuccess) onSuccess();
          else if (!inModal) navigate("/discount-master");
        } else {
          setLoading(false);
        }
      }
    } catch (error) {
      setLoading(false);
      console.log(error.message);
    }
  };

  const onFinishFailed = (errorInfo) => {
    setLoading(false);
    console.log("Failed:", errorInfo);
  };

  const handleDiscountTypeChange = (value) => {
    setDiscountType(value);
    form.setFieldsValue({ 
      customer_id: undefined,
      supplier_id: undefined 
    });
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === "bulk") {
      form.setFieldsValue({ publisher_id: undefined });
    } else {
      setSelectedPublishers([]);
      setPublisherDiscounts({});
      setFlatDiscountValue("");
      setApplyFlatDiscount(false);
    }
  };

  const commonFields = (
    <>
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Form.Item
            style={{ marginBottom: "10px" }}
            label="Discount Type"
            name="discount_type"
            rules={[{ required: true, message: "Please select discount type!" }]}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            <Select
              placeholder="Select Discount Type"
              onChange={handleDiscountTypeChange}
              disabled={editMode}
            >
              <Option value="sale">Sale (Customer Discount)</Option>
              <Option value="purchase">Purchase (Supplier Discount)</Option>
            </Select>
          </Form.Item>
        </Col>

        <Col span={12}>
          {discountType === "sale" && (
            <Form.Item
              style={{ marginBottom: "10px" }}
              label="Customer"
              name="customer_id"
              rules={[{ required: true, message: "Please select customer!" }]}
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
            >
              <Select
                placeholder="Select Customer"
                loading={!customers}
                onChange={(customerId) => {
                  if (activeTab === "single") prefillSingleDiscount(undefined, customerId, undefined);
                  else if (activeTab === "bulk") prefillBulkDiscounts();
                }}
              >
                {customers?.map((cust) => (
                  <Option key={cust.id} value={cust.id}>{cust.name}</Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {discountType === "purchase" && (
            <Form.Item
              style={{ marginBottom: "10px" }}
              label="Supplier"
              name="supplier_id"
              rules={[{ required: true, message: "Please select supplier!" }]}
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
            >
              <Select
                placeholder="Select Supplier"
                loading={!suppliers}
                onChange={(supplierId) => {
                  if (activeTab === "single") prefillSingleDiscount(undefined, undefined, supplierId);
                  else if (activeTab === "bulk") prefillBulkDiscounts();
                }}
              >
                {suppliers?.map((sup) => (
                  <Option key={sup.id} value={sup.id}>{sup.name}</Option>
                ))}
              </Select>
            </Form.Item>
          )}
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Form.Item
            style={{ marginBottom: "10px" }}
            label="Discount Unit"
            name="discount_unit"
            rules={[{ required: true, message: "Please select discount unit!" }]}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            <Select placeholder="Select Discount Unit">
              <Option value="percentage">Percentage (%)</Option>
              <Option value="fixed">Fixed Amount (₹)</Option>
            </Select>
          </Form.Item>
        </Col>

        {activeTab === 'bulk' && (
          <Col span={12} style={{ display: 'flex', alignItems: 'center', paddingTop: '10px' }}>
            <Button
              type="primary"
              onClick={() => setIsQuotationModalOpen(true)}
              style={{ marginTop: '20px' }}
            >
              Quotation
            </Button>
          </Col>
        )}
      </Row>
    </>
  );

  const sharedBottomFields = (
    <>
      <Form.Item style={{ marginBottom: "10px" }} label="Effective From" name="effective_from">
        <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
      </Form.Item>

      <Form.Item style={{ marginBottom: "10px" }} label="Effective To" name="effective_to">
        <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
      </Form.Item>

      <Form.Item style={{ marginBottom: "10px" }} label="Status" name="status" valuePropName="checked">
        <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
      </Form.Item>

      <Form.Item style={{ marginBottom: "10px" }} label="Description" name="description">
        <TextArea rows={3} placeholder="Enter description (optional)" />
      </Form.Item>
    </>
  );

  const renderSingleModeForm = () => (
    <>
      {commonFields}
      <Form.Item
        style={{ marginBottom: "10px" }}
        label="Publisher"
        name="publisher_id"
        rules={[{ required: true, message: "Please select publisher!" }]}
      >
        <Select
          placeholder="Select Publisher"
          loading={!publishers}
          onChange={(publisherId) => prefillSingleDiscount(publisherId, undefined, undefined)}
        >
          {publishers?.map((pub) => (
            <Option key={pub.id} value={pub.id}>{pub.name}</Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        style={{ marginBottom: "10px" }}
        label="Discount Value"
        name="discount_value"
        rules={[
          { required: true, message: "Please input discount value!" },
          ({ getFieldValue }) => ({
            validator(_, value) {
              const unit = getFieldValue("discount_unit");
              if (!value) return Promise.resolve();
              if (unit === "percentage" && (value < 0 || value > 100)) {
                return Promise.reject(new Error("Percentage must be between 0 and 100"));
              }
              if (unit === "fixed" && value < 0) {
                return Promise.reject(new Error("Fixed amount must be greater than or equal to 0"));
              }
              return Promise.resolve();
            },
          }),
        ]}
      >
        <Input type="number" step="0.01" placeholder="Enter discount value" />
      </Form.Item>

      {sharedBottomFields}
    </>
  );

  const renderBulkModeForm = () => (
    <>
      {commonFields}

      <div style={{ marginBottom: "20px", border: "1px solid #ddd", padding: "15px", borderRadius: "5px", backgroundColor: "#fafafa" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
          <Typography.Text strong>Bulk Select Publishers</Typography.Text>
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <Checkbox checked={applyFlatDiscount} onChange={handleApplyFlatDiscountToggle}>
              Apply Flat Discount:
            </Checkbox>
            <Input 
              type="number" 
              step="0.01" 
              placeholder="Enter flat value" 
              value={flatDiscountValue}
              onChange={handleFlatDiscountChange}
              style={{ width: "150px" }}
            />
          </div>
        </div>

        <Table
          rowSelection={pbSelection}
          columns={[
            {
              title: "Publisher",
              dataIndex: "name",
              key: "name",
              sorter: (a, b) => a.name.localeCompare(b.name),
              defaultSortOrder: 'ascend',
              filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
                const searchValue = selectedKeys[0] || '';
                const hasMatch = publishers?.some(pub => 
                  pub.name.toLowerCase().includes(searchValue.toLowerCase())
                );
                
                return (
                <div style={{ padding: 8 }}>
                  <Input
                    placeholder="Search publisher..."
                    value={searchValue}
                    autoFocus
                    onChange={(e) => {
                      setSelectedKeys(e.target.value ? [e.target.value] : []);
                      confirm({ closeDropdown: false });
                    }}
                    onPressEnter={() => confirm()}
                    style={{ width: 188, marginBottom: 8, display: 'block' }}
                  />
                  <div style={{ display: 'flex', gap: '8px', marginBottom: 8 }}>
                    <Button
                      type="primary"
                      onClick={() => confirm()}
                      size="small"
                      style={{ width: 90 }}
                    >
                      Close
                    </Button>
                    <Button onClick={() => {
                        clearFilters();
                        confirm({ closeDropdown: false });
                      }} size="small" style={{ width: 90 }}>
                      Reset
                    </Button>
                  </div>
                  {searchValue && !hasMatch && (
                    <Button 
                      type="dashed" 
                      onClick={() => {
                        setPrefillPublisherText(searchValue);
                        setIsAddPublisherModalOpen(true);
                      }} 
                      style={{ width: '100%' }}
                    >
                      + Add "{searchValue}"
                    </Button>
                  )}
                </div>
              );
             },
              onFilter: (value, record) => record.name.toLowerCase().includes(value.toLowerCase()),
            },
            {
              title: "Discount Value",
              key: "discount",
              width: 200,
              render: (_, record) => {
                const isSelected = selectedPublishers.includes(record.id);
                return (
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="0"
                    disabled={!isSelected}
                    value={publisherDiscounts[record.id] !== undefined ? publisherDiscounts[record.id] : ""}
                    onChange={(e) => handlePublisherDiscountChange(record.id, e.target.value)}
                  />
                );
              }
            }
          ]}
          dataSource={publishers?.slice().sort((a,b) => a.name.localeCompare(b.name)).map(p => ({ ...p, key: p.id })) || []}
          pagination={false}
          scroll={{ y: 250 }}
          size="small"
          style={{ backgroundColor: "#fff" }}
        />
      </div>

      {sharedBottomFields}
    </>
  );

  const formButtons = (
    <Form.Item
        wrapperCol={inModal ? { offset: 0, span: 24 } : { offset: 8, span: 16 }}
        style={inModal ? { marginBottom: 0 } : undefined}
      >
        {inModal ? (
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button
              onClick={() => setLoading(true)}
              htmlType="submit"
              loading={loading}
              style={{ backgroundColor: "#52c41a", borderColor: "#52c41a", color: "#fff" }}
            >
              Add Discount
            </Button>
            {onCancel && (
              <Button type="primary" danger onClick={onCancel}>
                Cancel
              </Button>
            )}
          </div>
        ) : (
          <Button onClick={() => setLoading(true)} type="primary" htmlType="submit" loading={loading}>
            {editMode ? "Update" : "Add"} Discount Master
          </Button>
        )}
      </Form.Item>
  );

  const renderFormContent = () => (
    <Form
      form={form}
      className=""
      name="basic"
      labelCol={inModal && activeTab === 'bulk' ? { span: 24 } : { span: 8 }}
      wrapperCol={inModal && activeTab === 'bulk' ? { span: 24 } : { span: 16 }}
      layout={inModal && activeTab === 'bulk' ? "vertical" : "horizontal"}
      initialValues={{ remember: true, discount_unit: "percentage", status: true }}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      autoComplete="off"
    >
      {!editMode ? (
        <Tabs activeKey={activeTab} onChange={handleTabChange}>
          <TabPane tab="Single Add" key="single">
            {renderSingleModeForm()}
          </TabPane>
          <TabPane tab="Bulk Add" key="bulk">
            {renderBulkModeForm()}
          </TabPane>
        </Tabs>
      ) : (
        renderSingleModeForm()
      )}
      
      {formButtons}
    </Form>
  );

  const renderQuotationModalContent = () => {
    let partyName = "";
    if (discountType === "sale") {
      const custId = form.getFieldValue("customer_id");
      const customer = customers?.find(c => c.id === custId);
      if (customer) partyName = customer.name;
    } else if (discountType === "purchase") {
      const supId = form.getFieldValue("supplier_id");
      const supplier = suppliers?.find(s => s.id === supId);
      if (supplier) partyName = supplier.name;
    }

    const modalData = publishers
      ?.filter(p => selectedPublishers.includes(p.id))
      ?.map(p => ({
        ...p,
        discount: publisherDiscounts[p.id] || 0
      }));

    return (
      <div style={{ padding: "10px" }}>
        <div style={{ marginBottom: "15px", fontSize: "16px", fontWeight: "bold" }}>
          {discountType === "sale" ? "Customer: " : discountType === "purchase" ? "Supplier: " : "Party: "}
          <span style={{ fontWeight: "normal" }}>{partyName || "Not Selected"}</span>
        </div>
        <Table
          dataSource={modalData?.map(p => ({ ...p, key: p.id })) || []}
          columns={[
            { title: "Publisher", dataIndex: "name", key: "name" },
            { 
              title: "Discount", 
              key: "discount",
              render: (_, record) => {
                 const unit = form.getFieldValue("discount_unit");
                 return `${record.discount || 0} ${unit === "percentage" ? "%" : "₹"}`
              }
            }
          ]}
          pagination={false}
          size="small"
          bordered
        />
      </div>
    );
  };

  if (inModal) {
    return (
      <>
        {renderFormContent()}
        <Modal
          title="Add Book Publisher"
          open={isAddPublisherModalOpen}
          onCancel={() => setIsAddPublisherModalOpen(false)}
          footer={null}
          destroyOnClose
        >
          <AddBookPublisher
            inModal
            prefillText={prefillPublisherText}
            onSuccess={() => {
              setIsAddPublisherModalOpen(false);
              setPrefillPublisherText("");
              dispatch(loadAllBookPublisher({ page: 1, limit: 1000 }));
            }}
            onCancel={() => setIsAddPublisherModalOpen(false)}
          />
        </Modal>

        <Modal
          title="Quotation Preview"
          open={isQuotationModalOpen}
          onCancel={() => setIsQuotationModalOpen(false)}
          footer={[
            <Button key="close" onClick={() => setIsQuotationModalOpen(false)}>
              Close
            </Button>
          ]}
          width={600}
          destroyOnClose
        >
          {renderQuotationModalContent()}
        </Modal>
      </>
    );
  }

  return (
    <Fragment>
      <Row className="mr-top" justify="space-between" gutter={[0, 30]}>
        <Col xs={24} sm={24} md={24} lg={14} xl={14} className="rounded column-design">
          <Card bordered={false} className="criclebox h-full">
            <Title
              level={4}
              className="text-center"
              style={{ backgroundColor: "#dadada", padding: "10px", margin: "0 !important" }}
            >
              {editMode ? "Update Discount" : "Add Discount"}
            </Title>
            {renderFormContent()}
          </Card>
        </Col>
      </Row>
      <Modal
        title="Add Book Publisher"
        open={isAddPublisherModalOpen}
        onCancel={() => setIsAddPublisherModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <AddBookPublisher
          inModal
          prefillText={prefillPublisherText}
          onSuccess={() => {
            setIsAddPublisherModalOpen(false);
            setPrefillPublisherText("");
            dispatch(loadAllBookPublisher({ page: 1, limit: 1000 }));
          }}
          onCancel={() => setIsAddPublisherModalOpen(false)}
        />
      </Modal>

      <Modal
        title="Quotation Preview"
        open={isQuotationModalOpen}
        onCancel={() => setIsQuotationModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsQuotationModalOpen(false)}>
            Close
          </Button>
        ]}
        width={600}
        destroyOnClose
      >
        {renderQuotationModalContent()}
      </Modal>
    </Fragment>
  );
};

export default AddDiscountMaster;
